import { CollapsibleList } from '@/features/proactive-agents/CollapsibleList';
import type {
  NudgeDelivery,
  NudgeStatus,
} from '@/features/proactive-agents/proactive-agents-types';
import { useNudgeStatusQuery } from '@/features/proactive-agents/useProactiveAgents';

const summarize = (status: NudgeStatus) => {
  const chatCount =
    status.waiting.length + status.delivered.length + status.failed.length;
  return `${status.delivered.length} of ${chatCount} delivered, ${status.waiting.length} waiting, ${status.failed.length} failed`;
};

const DeliveryList = ({
  title,
  deliveries,
}: {
  title: string;
  deliveries: NudgeDelivery[];
}) => (
  <CollapsibleList
    title={title}
    items={deliveries.map((delivery) => ({
      key: delivery.chat_id,
      text: `${delivery.email ?? 'A deleted chat or user'}${delivery.error ? `: ${delivery.error}` : ''}`,
    }))}
  />
);

export const NudgeDeliveryStatus = ({
  nudgeId,
  agent,
}: {
  nudgeId: string;
  agent: string;
}) => {
  const statusQuery = useNudgeStatusQuery(nudgeId);
  const status = statusQuery.data;
  return (
    <div role="status" className="grid gap-3 rounded-md bg-muted px-4 py-3">
      <p className="text-sm font-medium">
        Last message to {agent}:{' '}
        {status
          ? summarize(status)
          : statusQuery.error
            ? 'its delivery could not be loaded'
            : 'loading its delivery...'}
      </p>
      {statusQuery.error ? (
        <p role="alert" className="text-sm text-destructive">
          {statusQuery.error.message}
        </p>
      ) : null}
      {status?.waiting.length ? (
        <p className="text-xs text-muted-foreground">
          A chat shows as delivered once its turn has ended and the agent got
          the message. Turns start 10 seconds apart and wait for a busy chat.
          This updates on its own, about once a minute for a long send.
        </p>
      ) : null}
      {status ? (
        <>
          <DeliveryList title="Failed" deliveries={status.failed} />
          <DeliveryList title="Waiting" deliveries={status.waiting} />
          <DeliveryList title="Delivered" deliveries={status.delivered} />
        </>
      ) : null}
    </div>
  );
};
